'use client'

import { useQuery } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { supabase } from '@/lib/supabase'

export interface DashboardStats {
  streak: number
  studiedToday: boolean // Whether user has studied today (flame should be lit)
  wordsReviewedThisWeek: number
  cardsDueToday: number
  totalCardsStudied: number
}

/**
 * Calculate consecutive days with reviews (streak) and whether studied today
 * Returns { streak, studiedToday }
 * - streak: number of consecutive days (including today if studied)
 * - studiedToday: true if there's a review today (flame should be orange)
 */
function calculateStreak(reviewDates: string[]): {
  streak: number
  studiedToday: boolean
} {
  if (reviewDates.length === 0) return { streak: 0, studiedToday: false }

  // Get unique dates (in user's timezone, just use date part)
  const uniqueDates = [...new Set(reviewDates.map((d) => d.split('T')[0]))]
    .sort()
    .reverse()

  const today = new Date().toISOString().split('T')[0]
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]

  const studiedToday = uniqueDates[0] === today

  // Check if latest review is today or yesterday (streak is active)
  if (uniqueDates[0] !== today && uniqueDates[0] !== yesterday) {
    return { streak: 0, studiedToday: false }
  }

  let streak = 0
  let expectedDate = uniqueDates[0] === today ? today : yesterday

  for (const date of uniqueDates) {
    if (date === expectedDate) {
      streak++
      // Calculate previous day
      const d = new Date(expectedDate)
      d.setDate(d.getDate() - 1)
      expectedDate = d.toISOString().split('T')[0]
    } else if (date < expectedDate) {
      // Gap in dates, streak is broken
      break
    }
  }

  return { streak, studiedToday }
}

export function useDashboardStats() {
  const { user } = useAuth()

  return useQuery<DashboardStats>({
    queryKey: ['dashboard-stats', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return {
          streak: 0,
          studiedToday: false,
          wordsReviewedThisWeek: 0,
          cardsDueToday: 0,
          totalCardsStudied: 0
        }
      }

      // Get review logs for streak calculation (last 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString()
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString()
      const now = new Date().toISOString()

      const [
        { data: recentReviews, error: recentReviewsError },
        { count: weeklyCount, error: weeklyCountError },
        { count: dueCount, error: dueCountError },
        { count: totalStudied, error: totalStudiedError }
      ] = await Promise.all([
        supabase
          .from('study_review_logs')
          .select('review_time')
          .eq('user_id', user.id)
          .gte('review_time', thirtyDaysAgo)
          .order('review_time', { ascending: false }),
        supabase
          .from('study_review_logs')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .gte('review_time', weekAgo),
        supabase
          .from('study_cards')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .neq('state', 0)
          .lte('due', now),
        supabase
          .from('study_cards')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .gt('state', 0)
      ])

      const error =
        recentReviewsError ||
        weeklyCountError ||
        dueCountError ||
        totalStudiedError
      if (error) throw error

      const reviewDates = (recentReviews || []).map((r) => r.review_time)
      const { streak, studiedToday } = calculateStreak(reviewDates)

      return {
        streak,
        studiedToday,
        wordsReviewedThisWeek: weeklyCount || 0,
        cardsDueToday: dueCount || 0,
        totalCardsStudied: totalStudied || 0
      }
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true
  })
}
